import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Eye, ExternalLink, Download, FileText } from "lucide-react";
import { Badge } from "./ui/Badge";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { Field, Textarea } from "./ui/Field";
import { usePortal } from "./PortalContext";
import { openDocument, isDocLink, docCategoryLabel } from "./docs";
import { fmtDateHuman } from "./format";

/** Statusuri de revizuire setate de staff + rezultatul dat de client. */
export const DOC_REVIEW = {
  approval: { label: "Necesită aprobare", tone: "warning", actionable: true },
  review: { label: "De revizuit", tone: "accent", actionable: true },
  pending: { label: "În așteptare", tone: "amber-soft", actionable: false },
};
export const DOC_REVIEW_RESULT = {
  approved: { label: "Aprobat", tone: "success" },
  rejected: { label: "Respins", tone: "danger" },
  reviewed: { label: "Revizuit", tone: "success" },
};

/** Starea afișabilă a unui document (null dacă nu are flux de revizuire). */
export function docReviewState(doc) {
  if (!doc?.review_status) return null;
  if (doc.review_status === "done") {
    const r = DOC_REVIEW_RESULT[doc.review_result];
    return r ? { ...r, done: true, actionable: false } : null;
  }
  const s = DOC_REVIEW[doc.review_status];
  return s ? { ...s, done: false } : null;
}

const TONE_CLS = { "amber-soft": "!bg-[#FFF4D6] !text-[#9A6B00] !border-[#F3DDA0]" };

export function DocReviewBadge({ doc, size = "sm", className = "" }) {
  const s = docReviewState(doc);
  if (!s) return null;
  const custom = TONE_CLS[s.tone];
  return (
    <Badge tone={custom ? "warning" : s.tone} size={size} dot className={`${custom || ""} ${className}`} data-testid={`doc-review-badge-${doc.id}`}>
      {s.label}
    </Badge>
  );
}

/** Modal: clientul aprobă/respinge sau marchează ca revizuit documentul. */
export function DocReviewModal({ doc, open, onOpenChange, onDone }) {
  const { refresh } = usePortal();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  if (!doc) return null;
  const st = doc.review_status;

  async function act(decision) {
    if (decision === "rejected" && !comment.trim()) { setErr("Te rugăm să indici motivul respingerii."); return; }
    setErr(""); setBusy(decision);
    try {
      await api.post(`/client/documents/${doc.id}/review`, { decision, comment: comment.trim() });
      toast.success(decision === "approved" ? "Document aprobat" : decision === "rejected" ? "Document respins" : "Document marcat ca revizuit");
      setComment(""); onOpenChange(false); refresh(); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} testid="doc-review-modal"
      title={st === "approval" ? "Aprobare document" : st === "review" ? "Revizuire document" : "Document"}
      description={doc.project_name ? `${doc.project_name}${doc.project_code ? ` · ${doc.project_code}` : ""}` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="doc-review-cancel">Închide</Button>
          {st === "approval" && <Button variant="danger-soft" loading={busy === "rejected"} disabled={!!busy} onClick={() => act("rejected")} data-testid="doc-review-reject"><XCircle /> Respinge</Button>}
          {st === "approval" && <Button variant="success" loading={busy === "approved"} disabled={!!busy} onClick={() => act("approved")} data-testid="doc-review-approve"><CheckCircle2 /> Aprobă</Button>}
          {st === "review" && <Button loading={busy === "reviewed"} disabled={!!busy} onClick={() => act("reviewed")} data-testid="doc-review-mark"><Eye /> Marchează ca revizuit</Button>}
        </>
      }>
      <div className="flex items-start gap-3 p-3.5 rounded-[var(--cp-radius)] border border-cp-border bg-cp-bg-subtle/60">
        <span className="cp-tile is-sm"><FileText /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-cp-text break-words">{doc.name}</div>
          <div className="cp-meta mt-0.5">{docCategoryLabel(doc.category)}{doc.uploaded_at ? ` · publicat ${fmtDateHuman(doc.uploaded_at)}` : ""}</div>
          <div className="mt-2"><DocReviewBadge doc={doc} /></div>
        </div>
        <Button variant="outline" size="sm" onClick={() => openDocument(doc)} data-testid="doc-review-open">
          {isDocLink(doc) ? <><ExternalLink /> Deschide</> : <><Download /> Descarcă</>}
        </Button>
      </div>
      {(doc.review_note || doc.review_requested_by) && (
        <div className="mt-4 text-[13.5px] text-cp-text-2 leading-relaxed" data-testid="doc-review-note">
          {doc.review_note && <p className="text-cp-text">„{doc.review_note}”</p>}
          <p className="cp-meta mt-1">Solicitat de {doc.review_requested_by || "echipa Brisk"}{doc.review_requested_at ? ` · ${fmtDateHuman(doc.review_requested_at)}` : ""}</p>
        </div>
      )}
      {st === "pending" && <p className="cp-body-sm mt-4">Acest document este în așteptare la echipa Brisk. Nu este necesară o acțiune din partea ta momentan.</p>}
      {(st === "approval" || st === "review") && (
        <div className="mt-4">
          <Field label={st === "approval" ? "Comentariu (obligatoriu la respingere)" : "Comentariu (opțional)"} error={err}>
            <Textarea value={comment} onChange={(e) => { setComment(e.target.value); setErr(""); }} rows={3} placeholder="Observații pentru echipa Brisk…" data-testid="doc-review-comment" aria-invalid={!!err} />
          </Field>
        </div>
      )}
    </Modal>
  );
}
